import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { ForgedOpsAttribution } from "@/components/ForgedOpsAttribution";

/**
 * /legal/privacy — Privacy Policy.
 *
 * ForgedOps LLC owns and operates the FORGEDOPS platform. This page
 * describes how data is collected, used, and protected when you access
 * the platform. Treat the wording inside <article> as legal text —
 * do not edit phrasing without explicit owner approval.
 */
export default function PrivacyPolicy() {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <main className="min-h-screen bg-slate-50">
      <header className="border-b-2 border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/" className="text-slate-600 hover:text-slate-900" data-testid="privacy-back-link">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <ShieldCheck className="w-6 h-6 text-slate-700" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600 font-bold">
              Legal
            </div>
            <h1 className="font-display text-xl font-black text-slate-900">
              Privacy Policy
            </h1>
          </div>
        </div>
      </header>

      <article
        className="max-w-3xl mx-auto px-4 sm:px-6 py-8 prose prose-slate prose-sm sm:prose-base"
        data-testid="privacy-policy-page"
      >
        <p className="text-xs font-mono uppercase tracking-wide text-slate-500 mb-6">
          Effective: {today} · ForgedOps LLC · forgedopshq.com
        </p>

        <p>
          This Privacy Policy describes how <strong>ForgedOps LLC</strong>{" "}
          (&ldquo;<strong>ForgedOps</strong>,&rdquo; &ldquo;<strong>we</strong>,&rdquo;
          or &ldquo;<strong>us</strong>&rdquo;) collects, uses, and protects
          information when customers and authorized users access the
          <strong> FORGEDOPS</strong> field-operations and safety-documentation
          platform (the &ldquo;<strong>Platform</strong>&rdquo;).
        </p>

        <hr className="my-6 border-slate-200" />

        <h2>1. About ForgedOps LLC</h2>
        <p>
          ForgedOps LLC owns, builds, and operates the FORGEDOPS Platform.
          The Platform is delivered as customer-branded deployments to
          heavy-civil contractors. Each deployment is configured for the
          customer&rsquo;s operations, branding, and workflows.
        </p>
        <p>
          ForgedOps LLC stores and processes customer-submitted data solely
          to provide and maintain the Platform on the customer&rsquo;s behalf.
          ForgedOps does not sell, rent, or share customer data with third
          parties for marketing.
        </p>

        <hr className="my-6 border-slate-200" />

        <h2>2. Information We Collect</h2>
        <h3>Account Information</h3>
        <ul>
          <li>Name, email, role, and crew assignment of authorized users</li>
          <li>Authentication tokens (hashed) and session metadata</li>
        </ul>

        <h3>Operational Data</h3>
        <ul>
          <li>Daily reports, inspections, toolbox talks, incident records</li>
          <li>Equipment lists, walk-around results, OOS clearance records</li>
          <li>Photographs, signatures, and attached documents</li>
          <li>Project, job, and crew metadata</li>
        </ul>

        <h3>System Data</h3>
        <ul>
          <li>Browser, device, and IP metadata used for security and access logs</li>
          <li>Audit trails of submissions and routing</li>
        </ul>

        <hr className="my-6 border-slate-200" />

        <h2>3. How We Use Information</h2>
        <ul>
          <li>To deliver, maintain, secure, and support the Platform</li>
          <li>To route submissions to the correct folders and notify the right roles</li>
          <li>To generate PDFs, audit trails, and OSHA-defensible records</li>
          <li>To detect abuse, prevent fraud, and respond to security events</li>
          <li>To comply with applicable law and lawful requests</li>
        </ul>

        <hr className="my-6 border-slate-200" />

        <h2>4. Data Storage &amp; Security</h2>
        <p>
          ForgedOps LLC stores customer data in encrypted databases with
          regular backups and off-site mirroring. We use industry-standard
          measures including transport encryption, role-based access, and
          rate-limiting. No system is perfectly secure; ForgedOps does not
          guarantee absolute security.
        </p>

        <h2>5. Subprocessors</h2>
        <p>
          ForgedOps LLC uses a limited number of vetted infrastructure
          providers (cloud hosting, database, email/notification, monitoring).
          Subprocessors operate under written agreements that require them
          to protect data and use it only for the contracted purpose.
        </p>

        <h2>6. Data Retention</h2>
        <p>
          Operational records are retained for the lifetime of the customer
          deployment plus a backup window of 14 days unless the customer
          requests earlier deletion. Account data is retained while the user
          is authorized; access logs are retained for security and compliance.
        </p>

        <h2>7. Your Rights</h2>
        <p>
          Authorized users may request access, correction, or deletion of
          personal data. Requests should be sent through the customer&rsquo;s
          administrator, who will coordinate with ForgedOps LLC.
        </p>

        <h2>8. Changes</h2>
        <p>
          ForgedOps LLC may update this Privacy Policy. Material changes
          will be posted on this page with a new effective date.
        </p>

        <h2>9. Contact</h2>
        <p>
          Questions about this Policy or data handling should be directed to{" "}
          <a href="https://forgedopshq.com" target="_blank" rel="noopener noreferrer">
            forgedopshq.com
          </a>
          .
        </p>

        <p className="text-xs font-mono uppercase tracking-wide text-slate-500 mt-8">
          ForgedOps LLC · Operational infrastructure for heavy-civil contractors
        </p>

        <div className="mt-10 pt-6 border-t border-slate-200">
          <ForgedOpsAttribution variant="global" />
        </div>
      </article>
    </main>
  );
}
